import React from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NjkzMjc0MDB8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury Interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse delay-1000" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-sm text-white/90">Award-Winning Interior Design Studio</span>
          </div>

          {/* Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="block">Crafting</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-amber-400">
              Extraordinary
            </span>
            <span className="block">Living Spaces</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-xl leading-relaxed">
            {companyInfo.tagline}. We blend creativity with functionality to design spaces that inspire and delight.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => scrollToSection('#portfolio')}
              className="group bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/25 hover:-translate-y-1"
            >
              View Our Work
              <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
            <Button
              onClick={() => scrollToSection('#about')}
              variant="outline"
              className="group border-2 border-white/30 bg-white/5 backdrop-blur-sm text-white hover:bg-white hover:text-gray-900 px-8 py-6 rounded-full text-lg font-medium transition-all duration-300"
            >
              <Play className="mr-2 h-5 w-5" />
              Our Story
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 flex items-center gap-8 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-amber-400 border-2 border-white/50 flex items-center justify-center text-white text-xs font-bold"
                  >
                    {['P', 'R', 'A', 'K'][i - 1]}
                  </div>
                ))}
              </div>
              <div className="text-white">
                <p className="text-sm font-semibold">500+ Happy Clients</p>
                <p className="text-xs text-gray-400">Trust our expertise</p>
              </div>
            </div>
            <div className="h-12 w-px bg-white/20 hidden sm:block" />
            <div className="text-white">
              <p className="text-2xl font-bold text-amber-400">15+</p>
              <p className="text-xs text-gray-400">Years of Excellence</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/60 text-sm">Scroll to explore</span>
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full animate-scroll" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
